import { Image } from "@chakra-ui/react";
import { EventType, NFTEvent, NFTEventInfo } from "@models/event";
import { isEmpty } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useMemo } from "react";
import EventDetailContactUs from "./EventDetailContactUs";
import EventDetailCountdown from "./EventDetailCountdown";

export interface ActionButtonStyles {
  base: string;
  disabled: string;
  fill: string;
}

const EventDetailBanner = ({
  event,
  info,
  showContactUs = true,
  actionButtonStyles,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
  showContactUs?: boolean;
  actionButtonStyles?: ActionButtonStyles;
}) => {
  const router = useRouter();

  const actionUrl = useMemo(() => {
    if (event?.customizations?.overrideActionUrl?.startsWith("http"))
      return event.customizations.overrideActionUrl;
    const baseUrl = `${window.location.pathname}/checkout`;
    if (!router.query || isEmpty(router.query)) return baseUrl;
    try {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(router.query)) {
        if (!value) continue;
        if (Array.isArray(value)) {
          params.append(key, value.join(","));
        } else {
          params.append(key, value);
        }
      }
      return `${baseUrl}?${params.toString()}`;
    } catch (error) {
      return baseUrl;
    }
  }, [router.query, event?.customizations?.overrideActionUrl]);

  const actionText = useMemo(() => {
    if (event?.customizations?.overrideActionText)
      return event.customizations.overrideActionText;
    switch (event.type) {
      case EventType.BENEFIT:
        return "Get Benefit";
      case EventType.MEMBERSHIP:
        return "Get Membership";
      default:
        return "Get Ticket";
    }
  }, [event.type, event?.customizations?.overrideActionText]);

  const ActionButton = ({
    fill = true,
    disabled,
    children,
    styles = {
      base: "flex flex-row justify-center items-center flex-grow w-full relative border-primary-300 border-2 text-center font-semibold first:rounded-bl-xl last:rounded-br-xl cursor-pointer shadow-lg ",
      disabled: "bg-neutral-600",
      fill: "last:bg-primary-300 hover:bg-primary-300",
    },
  }: {
    fill?: boolean;
    disabled?: boolean;
    children?: ReactNode;
    styles?: ActionButtonStyles;
  }) => {
    return (
      <div
        className={
          styles.base + (disabled ? styles.disabled : fill ? styles.fill : "")
        }
      >
        {children}
      </div>
    );
  };

  const CustomActionButton = ({
    url,
    name,
    target: target = "_blank",
    actionButtonStyles,
  }: {
    url: string;
    name: string;
    target?: string;
    actionButtonStyles?: ActionButtonStyles;
  }) => {
    return (
      <ActionButton styles={actionButtonStyles}>
        <a
          className="p-4 w-full h-full flex flex-row items-center justify-center text-white"
          href={url}
          target={target}
          rel="noreferrer"
        >
          {name}
        </a>
      </ActionButton>
    );
  };

  return (
    <div className="max-w-full w-full mb-8 z-10">
      <div className="flex flex-col shadow-lg rounded-none rounded-t-xl overflow-hidden">
        <Image
          maxH={{ lg: "28rem" }}
          objectFit="fill"
          src={
            info.hero ||
            info.banner ||
            (info.gallery && info.gallery.length > 0
              ? info.gallery[0]
              : "/images/event-placeholder.svg")
          }
          alt={info.name}
        />
        <div className="flex flex-row w-full">
          <ActionButton styles={actionButtonStyles}>
            <Link href={actionUrl}>
              <a className="p-4 w-full h-full flex flex-row items-center justify-center text-white">
                {actionText}
              </a>
            </Link>
          </ActionButton>
          {!isEmpty(info.customActions) &&
            info.customActions!.map((action, i) => (
              <CustomActionButton
                key={i}
                url={action.url}
                name={action.name}
                actionButtonStyles={actionButtonStyles}
              />
            ))}
        </div>
      </div>

      {!!event.customizations?.showCountdown && (
        <EventDetailCountdown event={event} />
      )}

      {showContactUs && <EventDetailContactUs />}
    </div>
  );
};

export default EventDetailBanner;
